import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  PROJECT_FINAL_PAYMENT_LABELS,
  canManageBilling,
  projectFinalPayment,
  type ProjectFinalPayment,
  type AgentSummary, type Project, type ProjectMilestone, type ProjectStatus, type ProjectSubtask, type TicketSummary } from '@t3/shared';
import { ProjectBalanceCard } from '../components/ProjectBalanceCard.tsx';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { DeleteCascadeDialog } from '../components/DeleteCascadeDialog.tsx';
import { MilestoneList } from '../components/MilestoneList.tsx';
import { ProjectPlanSuggest } from '../components/ProjectPlanSuggest.tsx';
import { ProjectTicketsPanel } from '../components/ProjectTicketsPanel.tsx';
import { Select } from '../components/Select.tsx';
import { SubtaskList } from '../components/SubtaskList.tsx';

interface DetailResponse {
  project: Project;
  tickets: TicketSummary[];
  subtasks: ProjectSubtask[];
  milestones: ProjectMilestone[];
}

interface ProjectDeletePreview {
  project: { id: string; name: string; organizationName: string };
  counts: { subtasks: number; milestones: number; attachedTickets: number };
  attachedTicketSample: { displayNumber: number; subject: string }[];
}

export function ProjectDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const [data, setData] = useState<DetailResponse | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingDates, setEditingDates] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [cutoverDate, setCutoverDate] = useState('');
  const [savingDates, setSavingDates] = useState(false);
  const [editingSow, setEditingSow] = useState(false);
  const [sowSummary, setSowSummary] = useState('');
  const [savingSow, setSavingSow] = useState(false);
  const [sowError, setSowError] = useState<string | null>(null);
  const [deletePreview, setDeletePreview] = useState<ProjectDeletePreview | null>(null);
  const [deletePreviewBusy, setDeletePreviewBusy] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);

  async function load() {
    try {
      // The project route returns this project's tickets. Filtering the 200
      // most recent tickets app-wide in the browser dropped older ones.
      const [detail, subs, mls] = await Promise.all([
        apiFetch<{ project: Project; tickets: TicketSummary[] }>(`/api/projects/${id}`),
        apiFetch<{ subtasks: ProjectSubtask[] }>(`/api/projects/${id}/subtasks`),
        apiFetch<{ milestones: ProjectMilestone[] }>(`/api/projects/${id}/milestones`),
      ]);
      setData({
        project: detail.project,
        tickets: detail.tickets,
        subtasks: subs.subtasks,
        milestones: mls.milestones,
      });
      setDueDate(detail.project.dueDate ?? '');
      setCutoverDate(detail.project.cutoverDate ?? '');
      setSowSummary(detail.project.sowSummary ?? '');
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load project.');
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  useEffect(() => {
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents.filter((a) => a.isActive)))
      .catch(() => setAgents([]));
  }, []);

  async function changeAssignment(agentId: string) {
    try {
      await apiFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        json: { assignedAgentId: agentId || null },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update assignment.');
    }
  }

  async function changeStatus(newStatus: ProjectStatus) {
    try {
      await apiFetch(`/api/projects/${id}/status`, {
        method: 'PATCH',
        json: { status: newStatus },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update status.');
    }
  }

  async function changeBillingMode(mode: 'flat_fee' | 'block_hours') {
    try {
      await apiFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        json: { billingMode: mode },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update billing mode.');
    }
  }

  async function changeFinalPayment(state: ProjectFinalPayment) {
    try {
      await apiFetch(`/api/projects/${id}/final-payment`, {
        method: 'PUT',
        json: { state },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update payment status.');
    }
  }

  async function saveDates() {
    setSavingDates(true);
    try {
      await apiFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        json: {
          dueDate: dueDate || null,
          cutoverDate: cutoverDate || null,
        },
      });
      await load();
      setEditingDates(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update dates.');
    } finally {
      setSavingDates(false);
    }
  }

  async function saveSow() {
    setSowError(null);
    setSavingSow(true);
    try {
      await apiFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        json: {
          sowSummary: sowSummary.trim() || null,
        },
      });
      await load();
      setEditingSow(false);
    } catch (err) {
      setSowError(err instanceof ApiError ? err.message : 'Failed to update SOW details.');
    } finally {
      setSavingSow(false);
    }
  }

  async function openDeleteDialog() {
    setDeletePreviewError(null);
    setDeletePreviewBusy(true);
    try {
      const res = await apiFetch<ProjectDeletePreview>(`/api/projects/${id}/delete-preview`);
      setDeletePreview(res);
    } catch (err) {
      setDeletePreviewError(err instanceof ApiError ? err.message : 'Failed to load preview.');
    } finally {
      setDeletePreviewBusy(false);
    }
  }

  async function confirmDeleteProject() {
    await apiFetch(`/api/projects/${id}`, { method: 'DELETE' });
    navigate('/projects');
  }

  if (error) return <p className="text-sm text-[var(--color-danger)]">{error}</p>;
  if (!data) return <p className="text-[var(--color-muted)]">Loading…</p>;

  const { project, tickets, subtasks, milestones } = data;
  const usedH = project.consumedMinutes / 60;
  const remaining = project.budgetedHours - usedH;
  const pct = project.budgetedHours > 0 ? Math.min(100, (usedH / project.budgetedHours) * 100) : 0;

  const today = new Date(new Date().toDateString());
  const dueOverdue = project.dueDate !== null && new Date(project.dueDate) < today && project.status === 'active';
  const cutoverOverdue = project.cutoverDate !== null && new Date(project.cutoverDate) < today && project.status === 'active';
  const finalPayment = projectFinalPayment(project);
  const billing = canManageBilling(agent?.role);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/projects" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Projects
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-[var(--color-header)]">{project.name}</h1>
          {project.huduCompanyUrl && (
            <a
              href={project.huduCompanyUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
              title="Open this client in Hudu"
            >
              Open in Hudu →
            </a>
          )}
        </div>
        <p className="text-[var(--color-muted)]">
          {project.organizationName} · {project.billingMode === 'block_hours' ? 'Block hours' : 'Flat fee'} ·{' '}
          {project.assignedAgentName ? `Assigned to ${project.assignedAgentName}` : 'Unassigned'}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Status" value={project.status} />
        <Stat label="Budget" value={`${project.budgetedHours.toFixed(2)}h`} />
        <Stat label="Used" value={`${usedH.toFixed(2)}h`} />
        <Stat
          label="Remaining"
          value={`${remaining.toFixed(2)}h`}
          danger={remaining < 0 || pct >= 80}
        />
        <TaskGauge total={project.subtaskTotal} done={project.subtaskCompleted} />
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--color-muted)]">{pct.toFixed(0)}% consumed</span>
          {project.quoteTicketId && (
            <Link
              to={`/tickets/${project.quoteTicketId}`}
              className="text-sm text-[var(--color-t3-blue)] hover:underline"
            >
              Source quote →
            </Link>
          )}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
          <div
            className={`h-full ${pct >= 80 ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">Key dates</h2>
            {!editingDates && (
              <button
                type="button"
                onClick={() => setEditingDates(true)}
                className="text-sm text-[var(--color-t3-blue)] hover:underline"
              >
                Edit
              </button>
            )}
          </div>
          {editingDates ? (
            <div className="mt-3 space-y-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Due date</span>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Cutover date</span>
                <input
                  type="date"
                  value={cutoverDate}
                  onChange={(e) => setCutoverDate(e.target.value)}
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                />
              </label>
              <div className="flex gap-2">
                <Button onClick={() => void saveDates()} disabled={savingDates}>
                  {savingDates ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingDates(false);
                    setDueDate(project.dueDate ?? '');
                    setCutoverDate(project.cutoverDate ?? '');
                  }}
                  disabled={savingDates}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Due date</dt>
                <dd className={dueOverdue ? 'text-[var(--color-danger)] font-semibold' : ''}>
                  {project.dueDate ?? '—'}
                  {dueOverdue && ' (overdue)'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Cutover date</dt>
                <dd className={cutoverOverdue ? 'text-[var(--color-danger)] font-semibold' : ''}>
                  {project.cutoverDate ?? '—'}
                  {cutoverOverdue && ' (overdue)'}
                </dd>
              </div>
            </dl>
          )}
        </Card>

        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Project status & billing</h2>
          <div className="mt-3 space-y-3">
            <Select
              label="Status"
              value={project.status}
              onChange={(e) => void changeStatus(e.target.value as ProjectStatus)}
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            <Select
              label="Assigned to"
              value={project.assignedAgentId ?? ''}
              onChange={(e) => void changeAssignment(e.target.value)}
            >
              <option value="">Unassigned</option>
              {project.assignedAgentId &&
                !agents.some((a) => a.id === project.assignedAgentId) && (
                  <option value={project.assignedAgentId}>
                    {project.assignedAgentName ?? 'Current assignee'} (inactive)
                  </option>
                )}
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            {project.quoteTicketId ? (
              <p className="text-xs text-[var(--color-muted)]">
                Billing: <strong>Flat fee (project)</strong> — locked because this project came
                from a quote.
              </p>
            ) : (
              <Select
                label="Billing mode"
                value={project.billingMode}
                onChange={(e) => void changeBillingMode(e.target.value as 'flat_fee' | 'block_hours')}
              >
                <option value="block_hours">Block hours</option>
                <option value="flat_fee">Flat fee (project budget)</option>
              </Select>
            )}
            {finalPayment && (
              <div>
                <Select
                  label="Final payment"
                  value={finalPayment}
                  onChange={(e) => void changeFinalPayment(e.target.value as ProjectFinalPayment)}
                  disabled={finalPayment === 'collected' && !billing}
                >
                  <option value="not_due">{PROJECT_FINAL_PAYMENT_LABELS.not_due}</option>
                  <option value="to_collect">{PROJECT_FINAL_PAYMENT_LABELS.to_collect}</option>
                  <option value="collected" disabled={!billing}>
                    {PROJECT_FINAL_PAYMENT_LABELS.collected}
                  </option>
                </Select>
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  {finalPayment === 'collected'
                    ? `Collected${project.paymentCollectedAt ? ` ${formatDay(project.paymentCollectedAt)}` : ''}.`
                    : finalPayment === 'to_collect'
                      ? `Ready to bill since ${formatDay(project.finalPaymentRequestedAt!)}. Billing admins have been told.`
                      : 'Mark Collect final payment when the work is done, and billing will be told.'}
                  {!billing && finalPayment !== 'collected' && ' Only billing admins can mark it collected.'}
                  {project.quoteTicketId && finalPayment !== 'collected' && project.status === 'active' &&
                    ' Required before closing.'}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Money: billing roles only (the server refuses everyone else). */}
      {project.quoteTicketId && canManageBilling(agent?.role) && (
        <ProjectBalanceCard projectId={project.id} onPaid={load} />
      )}

      <Card>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Statement of work</h2>
          {!editingSow && (
            <button
              type="button"
              onClick={() => setEditingSow(true)}
              className="text-sm text-[var(--color-t3-blue)] hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        {editingSow ? (
          <div className="mt-3 space-y-3">
            {sowError && <p className="text-sm text-[var(--color-danger)]">{sowError}</p>}
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">SOW summary</span>
              <textarea
                value={sowSummary}
                onChange={(e) => setSowSummary(e.target.value)}
                rows={6}
                placeholder="Scope of work, deliverables, assumptions…"
                className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
              />
            </label>
            <div className="flex gap-2">
              <Button onClick={() => void saveSow()} disabled={savingSow}>
                {savingSow ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingSow(false);
                  setSowError(null);
                  setSowSummary(project.sowSummary ?? '');
                }}
                disabled={savingSow}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm">
            {project.sowSummary ? (
              <p className="whitespace-pre-wrap text-[var(--color-body)]">{project.sowSummary}</p>
            ) : (
              <p className="text-[var(--color-muted)]">No SOW details yet.</p>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Milestones</h2>
        <div className="mt-3">
          <ProjectPlanSuggest projectId={project.id} kind="milestones" milestones={milestones} onAdded={load} />
        </div>
        <div className="mt-3">
          <MilestoneList projectId={project.id} milestones={milestones} onChange={load} />
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Subtasks</h2>
        <div className="mt-3">
          <ProjectPlanSuggest projectId={project.id} kind="tasks" milestones={milestones} onAdded={load} />
        </div>
        <div className="mt-3">
          <SubtaskList projectId={project.id} subtasks={subtasks} onChange={load} />
        </div>
      </Card>

      <ProjectTicketsPanel project={project} tickets={tickets} onChange={load} />

      {isAdmin && (
        <Card className="border-[var(--color-danger)]/40">
          <h2 className="text-base font-semibold text-[var(--color-danger)]">Danger zone</h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Deleting a project permanently removes its subtasks and milestones. Attached tickets
            are <strong>kept</strong>, but their link to this project is cleared — their time will
            stop counting toward the project budget. This cannot be undone.
          </p>
          {deletePreviewError && (
            <p className="mt-2 text-sm text-[var(--color-danger)]">{deletePreviewError}</p>
          )}
          <Button
            variant="danger"
            className="mt-3"
            onClick={() => void openDeleteDialog()}
            disabled={deletePreviewBusy}
          >
            {deletePreviewBusy ? 'Loading…' : 'Delete project'}
          </Button>
        </Card>
      )}

      {deletePreview && (
        <DeleteCascadeDialog
          title={`Delete project "${deletePreview.project.name}"?`}
          intro={`This permanently removes the project from ${deletePreview.project.organizationName}. Attached tickets are not deleted — only unlinked.`}
          willBeRemoved={[
            { label: 'Subtasks', count: deletePreview.counts.subtasks },
            { label: 'Milestones', count: deletePreview.counts.milestones },
          ]}
          willBeDetached={
            deletePreview.counts.attachedTickets > 0
              ? [
                  {
                    label: `${deletePreview.counts.attachedTickets} attached ticket${deletePreview.counts.attachedTickets === 1 ? '' : 's'}`,
                    sublabel:
                      deletePreview.attachedTicketSample.length > 0
                        ? deletePreview.attachedTicketSample
                            .map((t) => `T-${t.displayNumber}: ${t.subject}`)
                            .join(' · ') +
                          (deletePreview.counts.attachedTickets > deletePreview.attachedTicketSample.length
                            ? ` (+ ${deletePreview.counts.attachedTickets - deletePreview.attachedTicketSample.length} more)`
                            : '')
                        : undefined,
                  },
                ]
              : undefined
          }
          confirmButtonLabel="Delete project"
          onConfirm={confirmDeleteProject}
          onClose={() => setDeletePreview(null)}
        />
      )}
    </div>
  );
}

function TaskGauge({ total, done }: { total: number; done: number }): React.ReactElement {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  // SVG ring gauge — radius 26, stroke 6
  const r = 26;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const color = total === 0
    ? 'rgb(125 125 125 / 0.4)'
    : pct >= 100
      ? 'rgb(34 197 94)'
      : 'rgb(0 174 239)';
  return (
    <Card>
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">Tasks</div>
      <div className="mt-1 flex items-center gap-3">
        <svg width="64" height="64" viewBox="0 0 64 64" className="flex-shrink-0">
          <circle cx="32" cy="32" r={r} fill="none" stroke="rgb(0 0 0 / 0.08)" strokeWidth={6} />
          <circle
            cx="32"
            cy="32"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={6}
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={c / 4}
            strokeLinecap="round"
            transform="rotate(-90 32 32)"
            style={{ transition: 'stroke-dasharray 200ms' }}
          />
          <text x="32" y="36" textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor">
            {pct}%
          </text>
        </svg>
        <div className="text-sm">
          {total === 0 ? (
            <span className="text-[var(--color-muted)]">No tasks</span>
          ) : (
            <>
              <div className="font-bold text-[var(--color-header)]">
                {done} of {total}
              </div>
              <div className="text-xs text-[var(--color-muted)]">complete</div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <Card className={danger ? 'border-[var(--color-danger)]/40' : ''}>
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]'}`}>
        {value}
      </div>
    </Card>
  );
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
